import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Building2,
  Contact,
  GitPullRequestArrow,
  LayoutDashboard,
  LogOut,
  Menu,
  UsersRound,
  Warehouse,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const navItems = (role) => [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/properties", label: "Properties", icon: Warehouse, testId: "nav-properties" },
  { to: "/leads", label: role === "admin" ? "Leads" : "My Leads", icon: Contact, testId: "nav-leads" },
  { to: "/deals", label: "Deals Pipeline", icon: GitPullRequestArrow, testId: "nav-deals" },
  ...(role === "admin"
    ? [{ to: "/users", label: "Manage Users", icon: UsersRound, testId: "nav-manage-users" }]
    : []),
];

const NavList = ({ role, onNavigate }) => (
  <nav className="flex-1 px-3 py-4 space-y-1">
    {navItems(role).map(({ to, label, icon: Icon, testId }) => (
      <NavLink
        key={to}
        to={to}
        end={to === "/"}
        onClick={onNavigate}
        data-testid={testId}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
            isActive
              ? "bg-emerald-900 text-white"
              : "text-emerald-100/70 hover:bg-emerald-900/50 hover:text-white"
          }`
        }
      >
        <Icon className="h-4 w-4" strokeWidth={1.5} />
        {label}
      </NavLink>
    ))}
  </nav>
);

const UserPanel = ({ currentUser, onLogout }) => (
  <div className="border-t border-emerald-900/60 p-4 space-y-3">
    <div>
      <p className="text-sm font-medium text-white" data-testid="sidebar-user-name">
        {currentUser?.full_name}
      </p>
      <p className="text-xs uppercase tracking-widest text-emerald-300/70" data-testid="sidebar-user-role">
        {currentUser?.role}
      </p>
    </div>
    <Button
      variant="outline"
      size="sm"
      onClick={onLogout}
      data-testid="logout-button"
      className="w-full rounded-lg bg-transparent border-emerald-700 text-emerald-100 hover:bg-emerald-900 hover:text-white transition-colors"
    >
      <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
      Log Out
    </Button>
  </div>
);

const Brand = () => (
  <div className="flex items-center gap-3 px-5 h-16 border-b border-emerald-900/60">
    <div className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center">
      <Building2 className="h-5 w-5 text-emerald-200" strokeWidth={1.5} />
    </div>
    <div className="leading-tight">
      <p className="font-heading text-sm font-semibold text-white">Aditya Property</p>
      <p className="text-[11px] uppercase tracking-widest text-emerald-300/70">Consultants</p>
    </div>
  </div>
);

export const Layout = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7] lg:flex">
      <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-[#04372b] min-h-screen sticky top-0 h-screen">
        <Brand />
        <NavList role={currentUser?.role} />
        <UserPanel currentUser={currentUser} onLogout={handleLogout} />
      </aside>

      <div className="lg:hidden flex items-center justify-between bg-[#04372b] px-4 h-14 sticky top-0 z-40">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-emerald-200" strokeWidth={1.5} />
          <span className="font-heading text-sm font-semibold text-white">Aditya Property Consultants</span>
        </div>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-nav-toggle" className="text-white hover:bg-emerald-900">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0 bg-[#04372b] border-emerald-900 flex flex-col">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <Brand />
            <NavList role={currentUser?.role} onNavigate={() => setMobileOpen(false)} />
            <UserPanel currentUser={currentUser} onLogout={handleLogout} />
          </SheetContent>
        </Sheet>
      </div>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
};
